import { useEffect, useRef, useState } from "react";
import ThemeToggle from "./ThemeToggle";
import ThemeChoiceDialog from "./ThemeChoiceDialog";
import { NavLink, Navigate, Outlet, useLocation } from "react-router-dom";
import {
  Bell,
  CalendarHeart,
  ChevronDown,
  FileText,
  HeartPulse,
  Home,
  LogOut,
  Menu,
  MessageSquare,
  Pill,
  Receipt,
  Settings,
} from "lucide-react";
import { useFamilyAuth } from "../context/FamilyAuthContext";
import FamilyLogo from "./FamilyLogo";
import { FamilyPushNotificationPrompt } from "./PushNotifications";
import FamilyNotificationBell from "./family/FamilyNotificationBell";

const primaryNav = [
  { to: "/family", label: "Home", icon: Home, end: true },
  { to: "/family/percorso", label: "Percorso", icon: HeartPulse, permission: "percorso:read" },
  { to: "/family/farmaci", label: "Farmaci", icon: Pill, permission: "terapia:read" },
  { to: "/family/documenti", label: "Documenti", icon: FileText, permission: "documenti:read" },
  { to: "/family/altro", label: "Altro", icon: Menu },
];
const extraNav = [
  { to: "/family/momenti", label: "Momenti", icon: CalendarHeart, permission: "momenti:read" },
  { to: "/family/aggiornamenti", label: "Aggiornamenti", icon: HeartPulse, permission: "aggiornamenti:read" },
  { to: "/family/richieste", label: "Messaggi e richieste", icon: MessageSquare, permission: "richieste:read" },
  { to: "/family/amministrazione", label: "Amministrazione", icon: Receipt, permission: "amministrazione:read" },
  { to: "/family/notifiche", label: "Notifiche", icon: Bell },
  { to: "/family/profilo", label: "Profilo e impostazioni", icon: Settings },
];

function isAltroSection(pathname) {
  if (pathname === "/family/altro") return true;
  return extraNav.some((item) => pathname === item.to || pathname.startsWith(`${item.to}/`));
}

function NavItem({ item, mobile = false }) {
  const { pathname } = useLocation();
  const Icon = item.icon;
  return <NavLink to={item.to} end={item.end} className={({ isActive }) => {
    const active = item.to === "/family/altro" ? isAltroSection(pathname) : isActive;
    return mobile
      ? `family-bottom-nav-item ${active ? "is-active" : ""}`
      : `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium ${active ? "bg-[#EDE9F6] text-[#6e48b5]" : "text-slate-600 hover:bg-slate-50"}`;
  }}>
    <Icon className={mobile ? "h-[18px] w-[18px]" : "h-[18px] w-[18px]"} strokeWidth={mobile ? 2.3 : 2} />
    <span className="truncate">{item.label}</span>
  </NavLink>;
}

export default function FamilyLayout() {
  const { loading, isAuthenticated, user, token, activeDelega, setActiveOspiteId, hasPermission, logout } = useFamilyAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const switcherRef = useRef(null);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onPointer = (event) => { if (!switcherRef.current?.contains(event.target)) setMenuOpen(false); };
    const onKey = (event) => { if (event.key === "Escape") setMenuOpen(false); };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onPointer); document.removeEventListener("keydown", onKey); };
  }, [menuOpen]);
  if (loading) return <div className="flex min-h-screen items-center justify-center bg-[#F7F5FB]"><span className="h-9 w-9 animate-spin rounded-full border-2 border-[#6e48b5] border-t-transparent" /></div>;
  if (!isAuthenticated) return <Navigate to="/family-login" replace />;
  const visiblePrimary = primaryNav.filter((item) => !item.permission || hasPermission(item.permission));
  const visibleExtra = extraNav.filter((item) => !item.permission || hasPermission(item.permission));
  return <div className="min-h-screen bg-[#edf3f9] text-[#263952]" style={{ minHeight: "100dvh" }}>
    <ThemeChoiceDialog />
    <header className="family-app-header">
      <div className="family-app-header-inner">
        <FamilyLogo className="h-8 max-w-[132px] shrink-0 sm:h-11 sm:max-w-[180px] md:max-w-none" />
        <p className="hidden min-w-0 border-l border-slate-300/80 pl-5 text-sm font-medium text-[#3d5574] md:block">Più vicini, ogni giorno.</p>
        <div ref={switcherRef} className="relative ml-auto min-w-0 max-w-[46%]">
          <button type="button" onClick={() => setMenuOpen(!menuOpen)} className="family-guest-switcher" aria-haspopup="menu" aria-expanded={menuOpen}>
            <span className="hidden text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400 sm:block">Stai seguendo</span>
            <span className="min-w-0 truncate">{activeDelega?.ospite_nome || "Seleziona ospite"}</span><ChevronDown className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" />
          </button>
          {menuOpen && <div role="menu" aria-label="Persona che stai seguendo" className="absolute right-0 z-50 mt-2 w-72 rounded-2xl border border-slate-100 bg-white p-2 shadow-xl">
            {(user?.deleghe || []).map((d) => <button type="button" role="menuitemradio" aria-checked={d.fascicolo_id === activeDelega?.fascicolo_id} key={d.id} onClick={() => { setActiveOspiteId(d.fascicolo_id); setMenuOpen(false); }} className={`w-full rounded-xl px-3 py-2.5 text-left text-sm hover:bg-[#EDE9F6] ${d.fascicolo_id === activeDelega?.fascicolo_id ? "bg-[#EDE9F6]/60" : ""}`}>
              <span className="block font-semibold">{d.ospite_nome}</span><span className="block text-xs text-slate-500">{d.struttura_nome}</span>
            </button>)}
          </div>}
        </div>
        <ThemeToggle className="hover:bg-white" />
        <FamilyNotificationBell />
      </div>
    </header>
    <div className="family-app-shell">
      <aside className="hidden w-56 shrink-0 self-stretch border-r border-slate-200/80 bg-white/70 px-3 py-6 xl:flex xl:flex-col">
        <nav className="space-y-1">{visiblePrimary.map((item) => <NavItem key={item.to} item={item} />)}</nav>
        <div className="mt-6 border-t border-slate-200/70 pt-4"><nav className="space-y-1">{visibleExtra.map((item) => <NavItem key={item.to} item={item} />)}</nav></div>
        <button type="button" onClick={logout} className="mt-auto flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-500 hover:bg-white"><LogOut className="h-[18px] w-[18px]" />Esci</button>
      </aside>
      <div className="min-w-0 flex-1 pb-24 xl:pb-8">
        <main className="mx-auto max-w-[920px] px-4 py-5 sm:px-6 sm:py-7"><Outlet /></main>
      </div>
    </div>
    <nav className="family-bottom-nav xl:hidden">{visiblePrimary.map((item) => <NavItem key={item.to} item={item} mobile />)}</nav>
    <FamilyPushNotificationPrompt token={token} />
  </div>;
}
